'use client';

import {
  Provider as RadixToastProvider,
  Viewport as RadixToastViewport,
  Root as RadixToastRoot,
  Action as RadixToastAction,
  Close as RadixToastClose,
  Title as RadixToastTitle,
  Description as RadixToastDescription,
} from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { forwardRef, type ElementRef, type ComponentPropsWithoutRef, type ReactElement } from 'react';

import { cn } from '@/lib/utils';

const ToastViewport = forwardRef<
  ElementRef<typeof RadixToastViewport>,
  ComponentPropsWithoutRef<typeof RadixToastViewport>
>(({ className, ...props }, ref) => (
  <RadixToastViewport
    ref={ref}
    className={cn(
      'fixed top-0 z-[100] flex max-h-dvh w-full flex-col-reverse p-4 sm:top-0 sm:right-0 sm:flex-col md:max-w-[420px]',
      className
    )}
    {...props}
  />
));

ToastViewport.displayName = 'ToastViewport';

const toastVariants = cva(
  'data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-top-full group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-6 pr-8 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none',
  {
    variants: {
      variant: {
        default: 'bg-background text-foreground border',
        warning: 'bg-warningBg text-warningFg',
        destructive: 'destructive border-destructive bg-destructive text-destructive-foreground group',
        success: 'bg-successBg text-successFg',
        alert: 'bg-alertBg text-alertFg',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const Toast = forwardRef<
  ElementRef<typeof RadixToastRoot>,
  ComponentPropsWithoutRef<typeof RadixToastRoot> & VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => (
  <RadixToastRoot ref={ref} className={cn(toastVariants({ variant }), className)} {...props} />
));

Toast.displayName = RadixToastRoot.displayName;

const ToastAction = forwardRef<ElementRef<typeof RadixToastAction>, ComponentPropsWithoutRef<typeof RadixToastAction>>(
  ({ className, ...props }, ref) => (
    <RadixToastAction
      ref={ref}
      className={cn(
        'ring-offset-background hover:bg-secondary focus:ring-ring group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
        className
      )}
      {...props}
    />
  )
);

ToastAction.displayName = RadixToastAction.displayName;

const ToastClose = forwardRef<ElementRef<typeof RadixToastClose>, ComponentPropsWithoutRef<typeof RadixToastClose>>(
  ({ className, ...props }, ref) => (
    <RadixToastClose
      ref={ref}
      className={cn(
        'text-foreground/50 hover:text-foreground absolute right-2 top-2 rounded-md p-1 opacity-0 transition-opacity focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600',
        className
      )}
      toast-close=""
      aria-label="close"
      {...props}
    >
      <X className="size-4" />
    </RadixToastClose>
  )
);

ToastClose.displayName = RadixToastClose.displayName;

const ToastTitle = forwardRef<ElementRef<typeof RadixToastTitle>, ComponentPropsWithoutRef<typeof RadixToastTitle>>(
  ({ className, ...props }, ref) => (
    <RadixToastTitle ref={ref} className={cn('text-sm font-semibold', className)} {...props} />
  )
);

ToastTitle.displayName = RadixToastTitle.displayName;

const ToastDescription = forwardRef<
  ElementRef<typeof RadixToastDescription>,
  ComponentPropsWithoutRef<typeof RadixToastDescription>
>(({ className, ...props }, ref) => (
  <RadixToastDescription ref={ref} className={cn('text-sm opacity-90', className)} {...props} />
));

ToastDescription.displayName = RadixToastDescription.displayName;

type ToastProps = ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  RadixToastProvider as ToastProvider,
  ToastViewport,
  Toast,
  ToastAction,
  ToastTitle,
  ToastDescription,
  ToastClose,
};
